import {
  CellContentCheckSimple,
  CommonRenderCellProps,
  CommonTableTemplate,
  TdWithLabel,
} from 'app/common/templates-next/table-template'

import { Td } from 'app/common/ui-components/table'
import { CellContentPhoto } from 'app/components/common/cellContents/CellContentPhoto'
import { Photo, SaveType } from 'app/dataProviders/dishes/DishesData'
import { PutValueParams } from 'app/dataTypes/dishes/field'
import { SaleType } from 'app/dataTypes/dishes/saleType'
import { DishId } from 'app/common/types/id'

import { DishesTableColumn, TableDataItem } from './dishesTableProps'
import DishesTableEditableProp from './cellContent/DishesTableEditableProp'
import DishesTableOptions from './cellContent/DishesTableOptions'
import { DishesTableProcessingLevel } from './cellContent/DishesTableProcessingLevel'
import { DishesTableUnit } from './cellContent/DishesTableUnit'
import { DishesTableSaleType } from './cellContent/DishesTableSaleType'
import { dishesFeatures } from '../../_features'
import { DishesTableBarCode } from './cellContent/DishesTableBarCode'
import { DishesTableTitleLayout } from './cellContent/DishesTableTitleLayout'
import { DishesTableEditableTitle } from './cellContent/DishesTableEditableTitle'

export const useDishesTableRenderCell =
  ({
    checkboxesAvailable,
    keepCheckboxesRegisteredOnDispose,
    locked,
    saleTypes,
    saleTypesLoading,
    photoUpdate,
    photoDelete,
    fieldUpdate,
    createTechCardLink,
  }: {
    checkboxesAvailable: boolean
    keepCheckboxesRegisteredOnDispose: boolean
    locked: boolean
    saleTypes: Array<SaleType>
    saleTypesLoading: boolean
    photoUpdate: (id: string, params: Photo) => Promise<boolean>
    photoDelete: (id: string) => Promise<boolean>
    fieldUpdate: (params: PutValueParams) => Promise<boolean>
    createTechCardLink?: (dishId: DishId) => string
  }) =>
  (params: CommonRenderCellProps<TableDataItem, DishesTableColumn>) => {
    const { columnKey, cellProps, rowData } = params
    const dishId = rowData[DishesTableColumn.Id]
    const dishTitle = rowData[DishesTableColumn.Title]

    if (columnKey === DishesTableColumn.Check) {
      const title = rowData[columnKey]
      return (
        <Td {...cellProps}>
          <CellContentCheckSimple
            hiddenLabel={title}
            id={dishId}
            keepRegisteredOnDispose={keepCheckboxesRegisteredOnDispose}
          />
        </Td>
      )
    }

    if (columnKey === DishesTableColumn.ProcessingLevel) {
      const value = rowData[columnKey]
      return (
        <Td {...cellProps}>
          <DishesTableProcessingLevel
            locked={locked}
            value={{ ready: true, raw: false }}
            onChange={() => new Promise(() => {})}
          />
        </Td>
      )
    }

    if (columnKey === DishesTableColumn.Title) {
      const value = rowData[columnKey]
      const submitTitle = (newValue: string) => {
        return fieldUpdate({
          valueType: 'common',
          id: dishId,
          field: 'title',
          value: newValue,
        })
      }

      return (
        <Td {...cellProps}>
          <DishesTableTitleLayout>
            <DishesTableEditableTitle value={value} locked={locked} onSubmit={submitTitle} />
            {dishesFeatures.barcode && <DishesTableBarCode title={value} />}
          </DishesTableTitleLayout>
        </Td>
      )
    }

    if (columnKey === DishesTableColumn.Nomenclature || columnKey === DishesTableColumn.Id) {
      const value = rowData[columnKey]
      return (
        <TdWithLabel {...cellProps} htmlFor={dishId} onlyTd={!checkboxesAvailable}>
          {value}
        </TdWithLabel>
      )
    }

    if (columnKey === DishesTableColumn.Photo) {
      const photoUrl = rowData[columnKey]
      return (
        <Td {...cellProps}>
          <CellContentPhoto
            photoUrl={photoUrl || ''}
            deleteConfirmText={`Вы уверены? Удалить изображение из блюда «${dishTitle}»?`}
            disabled={locked}
            onSaveImage={(photoParams: Photo) => photoUpdate(dishId, photoParams)}
            onDeleteImage={() => photoDelete(dishId)}
          />
        </Td>
      )
    }

    if (columnKey === DishesTableColumn.Portion || columnKey === DishesTableColumn.Cost) {
      const value = rowData[columnKey]
      const submitProp = (propName: 'portion' | 'cost') => {
        return (value: string, options: SaveType) => {
          return fieldUpdate({
            valueType: 'common',
            id: dishId,
            field: propName,
            value,
            changeItemTypes: options,
          })
        }
      }

      return (
        <Td {...cellProps}>
          {locked ? (
            value
          ) : (
            <DishesTableEditableProp value={value} onSubmit={submitProp(columnKey)} />
          )}
        </Td>
      )
    }

    if (columnKey === DishesTableColumn.Unit) {
      const value = rowData[columnKey]
      return (
        <Td {...cellProps}>
          <DishesTableUnit value="гр." onChange={() => new Promise(() => {})} locked={locked} />
        </Td>
      )
    }

    if (columnKey === DishesTableColumn.SaleType) {
      const value = rowData[columnKey]
      const submitSaleType = (saleTypeId: string) => {
        return fieldUpdate({
          valueType: 'common',
          id: dishId,
          field: 'sale_type_id',
          value: saleTypeId,
        })
      }

      return (
        <Td {...cellProps}>
          <DishesTableSaleType
            dishId={dishId}
            dishTitle={dishTitle}
            saleTypeId={value}
            saleTypesLoading={saleTypesLoading}
            saleTypes={saleTypes}
            locked={locked}
            submitSaleType={submitSaleType}
            submitKbzhu={async () => {
              return true
            }}
            showKbzhuButton={dishesFeatures.kbzhuAndTechCard}
            techCardLink={
              createTechCardLink && dishesFeatures.kbzhuAndTechCard
                ? createTechCardLink(dishId)
                : undefined
            }
          />
        </Td>
      )
    }

    if (columnKey === DishesTableColumn.Options) {
      return (
        <Td {...cellProps}>
          {!locked && <DishesTableOptions id={parseInt(dishId, 10)} title={dishTitle} />}
        </Td>
      )
    }

    return CommonTableTemplate.defaultRenderCell(params)
  }
